"use client";

import { ColumnDef } from "@tanstack/react-table";
import { Order } from "@/schemas/order";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SortableHeader } from "@/components/sortable_header";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { PizzaDTO } from "@/api/custom-schema";
import { useBranch } from "@/context/branch-context";
import { formatDailyCounter } from "@/lib/counter-format";
import { formatOrderStatus, OrderStatusValue } from "@/lib/order-status";
import { splitOrderItems } from "@/lib/order-items";
import { StatusDot, statusTone } from "@/components/status-badge";
import { copyToClipboard } from "@/lib/clipboard";
import { shortDateTime } from "@/lib/date-format";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";

export const OrderBranchName = ({ branchId }: { branchId: string | undefined }) => {
  const { branches } = useBranch();
  return (
    <span className="whitespace-nowrap text-sm">
      {branches?.find((branch) => branch.bb_branch_id === branchId)?.name || "-"}
    </span>
  );
};

/** One dot per pizza of the order: green OK, red NOK, grey not captured / not evaluated. */
export function OrderPizzaDots({
  order,
  pizzasById,
}: {
  order: Order;
  pizzasById: Map<string, PizzaDTO>;
}) {
  const { t } = useLocalization();
  const [pizzaItems] = splitOrderItems(order.items);
  if (pizzaItems.length === 0) {
    return <span className="text-xs text-muted-foreground">–</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {pizzaItems.map((item) => {
        const pizza = pizzasById.get(item.id);
        const status = pizza?.evaluation?.metadata?.overall_evaluation?.status;
        const statusText = !pizza
          ? t("orders.tile.notCaptured")
          : status === "OK"
            ? t("status.ok")
            : status === "NOK"
              ? t("status.nok")
              : (status ?? t("status.notEvaluated"));
        return (
          <StatusDot
            key={item.id}
            tone={pizza ? statusTone(status) : "neutral"}
            title={`${item.name}: ${statusText}`}
          />
        );
      })}
      <span className="ml-1 text-xs tabular-nums text-muted-foreground">{pizzaItems.length}</span>
    </div>
  );
}

export const columns: (
  pizzasById: Map<string, PizzaDTO>,
) => ColumnDef<Order>[] = (pizzasById) => [
  {
    accessorKey: "daily_counter",
    header: ({ column }) => <SortableHeader column={column} label={translate("orders.columns.orderNumber")} />,
    cell: ({ row }) => {
      const order = row.original;
      return (
        <span className="font-mono font-semibold">
          {formatDailyCounter(order.daily_counter ?? order.formatted_id)}
        </span>
      );
    },
  },
  {
    accessorKey: "sent_at",
    header: ({ column }) => <SortableHeader column={column} label={translate("orders.columns.sentAt")} />,
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">{shortDateTime(info.getValue() as string)}</span>
    ),
  },
  {
    accessorKey: "location.id",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.branch")} />,
    cell: (info) => <OrderBranchName branchId={info.getValue() as string | undefined} />,
  },
  {
    id: "pizzas",
    header: () => <span className="text-xs font-medium">{translate("common.pizzas")}</span>,
    cell: ({ row }) => <OrderPizzaDots order={row.original} pizzasById={pizzasById} />,
    enableSorting: false,
  },
  {
    accessorKey: "items",
    header: () => <span className="text-xs font-medium">{translate("orders.columns.items")}</span>,
    cell: (info) => {
      const items = info.getValue() as unknown[];
      return <span className="tabular-nums">{items?.length ?? 0}</span>;
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.status")} />,
    cell: (info) => (
      <span className="rounded-md border px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
        {formatOrderStatus(info.getValue() as OrderStatusValue)}
      </span>
    ),
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <SortableHeader column={column} label={translate("orders.columns.createdAt")} />,
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">{shortDateTime(info.getValue() as string)}</span>
    ),
  },
  {
    accessorKey: "id",
    header: ({ column }) => <SortableHeader column={column} label={translate("orders.columns.id")} />,
    cell: (info) => {
      const id = info.getValue() as string;
      return (
        <span className="font-mono text-xs text-muted-foreground" title={id}>
          {id.slice(0, 8)}
        </span>
      );
    },
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const order = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{translate("orders.actions.openMenu")}</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{translate("common.actions")}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => copyToClipboard(order.id, translate("orders.columns.orderId"))}>
              {translate("orders.actions.copyId")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => copyToClipboard(JSON.stringify(order), translate("orders.actions.orderData"))}
            >
              {translate("orders.actions.copyData")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
